import React, { useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Download, AlertTriangle } from "lucide-react";
import { todayISO } from "@/lib/utils-app";

const PRESET_CAUSAS = [
  "Clases previas al examen práctico de circulación de la Dirección General de Tráfico (DGT)",
  "Convocatoria oficial al examen práctico de circulación de la Dirección General de Tráfico (DGT)",
  "Convocatoria oficial al examen teórico de circulación de la Dirección General de Tráfico (DGT)",
];

const MESES_ES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

function formatFechaLarga(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getDate()} de ${MESES_ES[d.getMonth()]} de ${d.getFullYear()}`;
}

function fullName(student) {
  if (!student) return "";
  const parts = [student.name, student.primer_apellido, student.segundo_apellido]
    .filter((p) => p && String(p).trim())
    .map((p) => String(p).trim());
  return parts.join(" ");
}

export default function JustificanteAusenciaForm({ students, defaultStudentId = "", lockStudent = false }) {
  const [studentId, setStudentId] = useState(defaultStudentId);
  const [fecha, setFecha] = useState(todayISO());
  const [causaMode, setCausaMode] = useState("preset"); // preset | custom
  const [causaPreset, setCausaPreset] = useState(PRESET_CAUSAS[0]);
  const [causaCustom, setCausaCustom] = useState("");
  const [generating, setGenerating] = useState(false);

  const student = useMemo(
    () => students.find((s) => s.id === studentId),
    [students, studentId]
  );

  const causa = causaMode === "custom" ? causaCustom.trim() : causaPreset;

  const canGenerate = studentId && fecha && causa;

  const generate = async () => {
    if (!canGenerate) return;
    setGenerating(true);
    try {
      const res = await api.post(
        "/documents/justificante-ausencia",
        { student_id: studentId, fecha, causa },
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
      toast.success("Justificante generado");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Error generando PDF");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="justificante-ausencia-form">
      {/* ---- FORM ---- */}
      <div className="card-surface p-4 space-y-4">
        {!lockStudent && (
          <div>
            <label className="text-xs font-semibold mb-1 block">Alumno *</label>
            <select
              value={studentId}
              onChange={(e) => setStudentId(e.target.value)}
              data-testid="ja-student"
              className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-10 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="">— Selecciona un alumno —</option>
              {students.map((s) => (
                <option key={s.id} value={s.id}>
                  {fullName(s) || s.name}
                </option>
              ))}
            </select>
          </div>
        )}
        {lockStudent && student && (
          <div className="rounded-lg bg-orange-50 border border-orange-200 px-3 py-2 text-sm">
            <span className="text-xs font-semibold text-neutral-600 block mb-0.5">Alumno</span>
            <span className="font-semibold">{fullName(student) || student.name}</span>
            {student.dni && <span className="text-xs text-neutral-500 ml-2">· DNI {student.dni}</span>}
          </div>
        )}

        <div>
          <label className="text-xs font-semibold mb-1 block">Fecha de la ausencia *</label>
          <input
            type="date"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            data-testid="ja-fecha"
            className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-10 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
        </div>

        <div className="space-y-2">
          <div className="text-xs font-semibold">Motivo de la ausencia *</div>
          {PRESET_CAUSAS.map((c, i) => (
            <label
              key={i}
              className={
                "flex items-start gap-2 rounded-lg border p-2.5 cursor-pointer text-sm transition-colors " +
                (causaMode === "preset" && causaPreset === c
                  ? "border-orange-500 bg-orange-50/40"
                  : "border-neutral-200 hover:bg-neutral-50")
              }
            >
              <input
                type="radio"
                name="causa"
                checked={causaMode === "preset" && causaPreset === c}
                onChange={() => { setCausaMode("preset"); setCausaPreset(c); }}
                data-testid={`ja-causa-preset-${i}`}
                className="mt-0.5 accent-orange-500"
              />
              <span>{c}</span>
            </label>
          ))}
          <label
            className={
              "flex items-start gap-2 rounded-lg border p-2.5 cursor-pointer text-sm transition-colors " +
              (causaMode === "custom"
                ? "border-orange-500 bg-orange-50/40"
                : "border-neutral-200 hover:bg-neutral-50")
            }
          >
            <input
              type="radio"
              name="causa"
              checked={causaMode === "custom"}
              onChange={() => setCausaMode("custom")}
              data-testid="ja-causa-custom"
              className="mt-0.5 accent-orange-500"
            />
            <span className="font-semibold">Texto libre</span>
          </label>
          {causaMode === "custom" && (
            <textarea
              value={causaCustom}
              onChange={(e) => setCausaCustom(e.target.value)}
              placeholder="Describe el motivo de la ausencia"
              rows={3}
              autoFocus
              data-testid="ja-causa-input"
              className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          )}
        </div>

        {student && !student.dni && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 text-amber-900 text-xs px-3 py-2 flex items-start gap-2">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>El alumno no tiene DNI registrado. Aparecerá un guión en el PDF. Puedes añadirlo desde su ficha.</span>
          </div>
        )}

        <button
          type="button"
          onClick={generate}
          disabled={!canGenerate || generating}
          data-testid="ja-generate"
          className="btn-primary w-full justify-center"
        >
          <Download className="h-4 w-4" />
          {generating ? "Generando…" : "Generar PDF"}
        </button>
      </div>

      {/* ---- LIVE PREVIEW ---- */}
      <div className="card-surface p-0 overflow-hidden">
        <div className="bg-neutral-100 border-b border-neutral-200 px-4 py-2 text-xs font-semibold text-neutral-600 flex items-center justify-between">
          <span>Vista previa</span>
          <span className="text-[10px] text-neutral-400 font-normal">A4 simulado</span>
        </div>
        <div className="bg-white p-6 sm:p-8 min-h-[480px] text-[12px] leading-relaxed">
          <div className="text-center">
            <h3 className="text-lg font-semibold tracking-wide">JUSTIFICANTE DE AUSENCIA</h3>
          </div>
          <p className="mt-4">
            La Dirección de la Autoescuela <strong>NATUR AUTOESCUELA</strong>
          </p>
          <h4 className="text-center mt-6 mb-3 font-medium tracking-[2px]">HACE CONSTAR</h4>
          <p className="text-justify">
            Que el/la alumno/a{" "}
            <strong>{fullName(student) || "[ alumno ]"}</strong>,
            con DNI/NIE <strong>{student?.dni || "—"}</strong>,
            y matriculado/a en este centro de formación vial, deberá ausentarse
            de sus actividades académicas y/o laborales el día{" "}
            <strong>{formatFechaLarga(fecha)}</strong>,
            con motivo de:
          </p>
          <p className="my-4 ml-3 pl-3 border-l-[3px] border-neutral-900 font-medium">
            {causa || <span className="text-neutral-400">[ causa de la ausencia ]</span>}
          </p>
          <p className="text-justify">
            Se expide el presente justificante a petición del interesado/a para
            que surta los efectos oportunos ante quien corresponda.
          </p>
          <p className="mt-6">
            En <strong>Madrid</strong>, a {formatFechaLarga(fecha)}.
          </p>
          <div className="mt-10 text-right">
            <div className="inline-block w-56 border-t border-neutral-900"></div>
            <div className="text-[10px] text-neutral-500 mt-1">Firma y sello — La Dirección</div>
          </div>
        </div>
      </div>
    </div>
  );
}
